import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  CalendarSubscription,
  CalendarSubscriber,
} from 'commoncal-shared';

export function useSubscriptions(
  calendarId: string | undefined
) {
  return useQuery({
    queryKey: ['subscriptions', calendarId],
    queryFn: () =>
      calendarId
        ? api.subscriptions.list(calendarId)
        : Promise.resolve(null),
    enabled: Boolean(calendarId),
    select: data => data?.subscriptions ?? [],
  });
}

export function useSubscribers(
  calendarId: string | undefined
) {
  return useQuery<
    { subscribers: CalendarSubscriber[] } | null,
    Error,
    CalendarSubscriber[]
  >({
    queryKey: ['subscribers', calendarId],
    queryFn: () =>
      calendarId
        ? api.subscribers.list(calendarId)
        : Promise.resolve(null),
    enabled: Boolean(calendarId),
    select: data => {
      const list = data?.subscribers ?? [];
      console.log(
        '[useSubscribers] calendarId=%s count=%d',
        calendarId,
        list.length,
        list
      );
      return list;
    },
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      join_token: string;
      subscriber_calendar_id: string;
      color_override?: string;
    }) => {
      console.log(
        '[useSubscriptions] Creating subscription:',
        {
          join_token: body.join_token,
          subscriber_calendar_id:
            body.subscriber_calendar_id,
        }
      );
      return api.subscriptions.create(body);
    },
    onSuccess: (_data, vars) => {
      console.log(
        '[useSubscriptions] Subscription created successfully'
      );
      qc.invalidateQueries({
        queryKey: [
          'subscriptions',
          vars.subscriber_calendar_id,
        ],
      });
      // Refresh calendars list so the new subscription appears in sidebar
      qc.invalidateQueries({
        queryKey: ['calendars'],
      });
      // Refresh calendar-access so isMember updates (clears guestCalendar)
      qc.invalidateQueries({
        queryKey: ['calendar-access'],
      });
    },
    onError: (error, vars) => {
      console.error(
        '[useSubscriptions] Subscription creation failed:',
        {
          error:
            error instanceof Error ? error.message : error,
          join_token: vars.join_token,
          subscriber_calendar_id:
            vars.subscriber_calendar_id,
        }
      );
    },
  });
}

export function useUpdateSubscription(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      color_override?: string | null;
    }) => api.subscriptions.update(id, body),
    onMutate: async ({ id, ...patch }) => {
      const key = ['subscriptions', calendarId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<{
        subscriptions: CalendarSubscription[];
      }>(key);
      qc.setQueryData<{
        subscriptions: CalendarSubscription[];
      }>(key, old => {
        if (!old) return old;
        return {
          subscriptions: old.subscriptions.map(s =>
            s.id === id ? { ...s, ...patch } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          ['subscriptions', calendarId],
          ctx.previous
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({
        queryKey: ['subscriptions', calendarId],
      });
    },
  });
}

export function useDeleteSubscription(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.subscriptions.delete(id),
    onMutate: async id => {
      const key = ['subscriptions', calendarId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<{
        subscriptions: CalendarSubscription[];
      }>(key);
      qc.setQueryData<{
        subscriptions: CalendarSubscription[];
      }>(key, old => {
        if (!old) return old;
        return {
          subscriptions: old.subscriptions.filter(
            s => s.id !== id
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          ['subscriptions', calendarId],
          ctx.previous
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({
        queryKey: ['subscriptions', calendarId],
      });
      // Refresh calendars list so the sidebar updates
      qc.invalidateQueries({
        queryKey: ['calendars'],
      });
      // Refresh calendar-access so isMember updates (for re-join via token)
      qc.invalidateQueries({
        queryKey: ['calendar-access'],
      });
    },
  });
}
